'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["in", "unmountOnExit", "animateOpacity", "startingHeight", "endingHeight", "style", "className", "transition", "transitionEnd"],
  _excluded2 = ["unmountOnExit", "in", "className", "transition", "transitionEnd", "delay"],
  _excluded3 = ["unmountOnExit", "in", "reverse", "initialScale", "className", "transition", "transitionEnd", "delay"],
  _excluded4 = ["unmountOnExit", "in", "reverse", "className", "offsetX", "offsetY", "transition", "transitionEnd", "delay"],
  _excluded5 = ["direction", "style", "unmountOnExit", "in", "className", "transition", "transitionEnd", "delay", "motionProps"];
import { warn, cx } from "../shared-utils";
import { AnimatePresence, motion } from "../../framer-motion";
import { forwardRef, useState, useEffect } from 'react';
import { jsx } from 'react/jsx-runtime';

// src/transition-utils.ts
var TRANSITION_EASINGS = {
  ease: [0.25, 0.1, 0.25, 1],
  easeIn: [0.4, 0, 1, 1],
  easeOut: [0, 0, 0.2, 1],
  easeInOut: [0.4, 0, 0.2, 1]
};
var TRANSITION_VARIANTS = {
  scale: {
    enter: {
      scale: 1
    },
    exit: {
      scale: 0.95
    }
  },
  fade: {
    enter: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  },
  pushLeft: {
    enter: {
      x: "100%"
    },
    exit: {
      x: "-30%"
    }
  },
  pushRight: {
    enter: {
      x: "-100%"
    },
    exit: {
      x: "30%"
    }
  },
  pushUp: {
    enter: {
      y: "100%"
    },
    exit: {
      y: "-30%"
    }
  },
  pushDown: {
    enter: {
      y: "-100%"
    },
    exit: {
      y: "30%"
    }
  },
  slideLeft: {
    position: {
      left: 0,
      top: 0,
      bottom: 0,
      width: "100%"
    },
    enter: {
      x: 0,
      y: 0
    },
    exit: {
      x: "-100%",
      y: 0
    }
  },
  slideRight: {
    position: {
      right: 0,
      top: 0,
      bottom: 0,
      width: "100%"
    },
    enter: {
      x: 0,
      y: 0
    },
    exit: {
      x: "100%",
      y: 0
    }
  },
  slideUp: {
    position: {
      top: 0,
      left: 0,
      right: 0,
      maxWidth: "100vw"
    },
    enter: {
      x: 0,
      y: 0
    },
    exit: {
      x: 0,
      y: "-100%"
    }
  },
  slideDown: {
    position: {
      bottom: 0,
      left: 0,
      right: 0,
      maxWidth: "100vw"
    },
    enter: {
      x: 0,
      y: 0
    },
    exit: {
      x: 0,
      y: "100%"
    }
  }
};
function getSlideTransition(options) {
  var _a;
  const side = (_a = options == null ? void 0 : options.direction) != null ? _a : "right";
  switch (side) {
    case "right":
      return TRANSITION_VARIANTS.slideRight;
    case "left":
      return TRANSITION_VARIANTS.slideLeft;
    case "bottom":
      return TRANSITION_VARIANTS.slideDown;
    case "top":
      return TRANSITION_VARIANTS.slideUp;
    default:
      return TRANSITION_VARIANTS.slideRight;
  }
}
var TRANSITION_DEFAULTS = {
  enter: {
    duration: 0.2,
    ease: TRANSITION_EASINGS.easeOut
  },
  exit: {
    duration: 0.1,
    ease: TRANSITION_EASINGS.easeIn
  }
};
var withDelay = {
  enter: (transition, delay) => Object.assign({}, transition, {
    delay: typeof delay === "number" ? delay : delay == null ? void 0 : delay["enter"]
  }),
  exit: (transition, delay) => Object.assign({}, transition, {
    delay: typeof delay === "number" ? delay : delay == null ? void 0 : delay["exit"]
  })
};
var isNumeric = value => value != null && parseInt(value.toString(), 10) > 0;
var defaultTransitions = {
  exit: {
    height: {
      duration: 0.2,
      ease: TRANSITION_EASINGS.ease
    },
    opacity: {
      duration: 0.3,
      ease: TRANSITION_EASINGS.ease
    }
  },
  enter: {
    height: {
      duration: 0.3,
      ease: TRANSITION_EASINGS.ease
    },
    opacity: {
      duration: 0.4,
      ease: TRANSITION_EASINGS.ease
    }
  }
};
var variants$4 = {
  exit: ({
    animateOpacity,
    startingHeight,
    transition,
    transitionEnd,
    delay
  }) => {
    var _a;
    return Object.assign({}, animateOpacity && {
      opacity: isNumeric(startingHeight) ? 1 : 0
    }, {
      height: startingHeight,
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.exit,
      transition: (_a = transition == null ? void 0 : transition.exit) != null ? _a : withDelay.exit(defaultTransitions.exit, delay)
    });
  },
  enter: ({
    animateOpacity,
    endingHeight,
    transition,
    transitionEnd,
    delay
  }) => {
    var _a;
    return Object.assign({}, animateOpacity && {
      opacity: 1
    }, {
      height: endingHeight,
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.enter,
      transition: (_a = transition == null ? void 0 : transition.enter) != null ? _a : withDelay.enter(defaultTransitions.enter, delay)
    });
  }
};
var Collapse = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      in: isOpen,
      unmountOnExit,
      animateOpacity = true,
      startingHeight = 0,
      endingHeight = "auto",
      style,
      className,
      transition,
      transitionEnd
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setMounted(true);
    });
    return () => clearTimeout(timeout);
  }, []);
  warn({
    condition: Number(startingHeight) > 0 && !!unmountOnExit,
    message: `startingHeight and unmountOnExit are mutually exclusive. You can't use them together`
  });
  const hasStartingHeight = parseFloat(startingHeight.toString()) > 0;
  const custom = {
    startingHeight,
    endingHeight,
    animateOpacity,
    transition: !mounted ? {
      enter: {
        duration: 0
      }
    } : transition,
    transitionEnd: {
      enter: transitionEnd == null ? void 0 : transitionEnd.enter,
      exit: unmountOnExit ? transitionEnd == null ? void 0 : transitionEnd.exit : Object.assign({}, transitionEnd == null ? void 0 : transitionEnd.exit, {
        display: hasStartingHeight ? "block" : "none"
      })
    }
  };
  const show = unmountOnExit ? isOpen : true;
  const animate = isOpen || unmountOnExit ? "enter" : "exit";
  return /* @__PURE__ */jsx(AnimatePresence, {
    initial: false,
    custom,
    children: show && /* @__PURE__ */jsx(motion.div, Object.assign({
      ref
    }, rest, {
      className: cx("chakra-collapse", className),
      style: Object.assign({
        overflow: "hidden",
        display: "block"
      }, style),
      custom,
      variants: variants$4,
      initial: unmountOnExit ? "exit" : false,
      animate,
      exit: "exit"
    }))
  });
});
Collapse.displayName = "Collapse";
var variants$3 = {
  enter: ({
    transition,
    transitionEnd,
    delay
  } = {}) => {
    var _a;
    return {
      opacity: 1,
      transition: (_a = transition == null ? void 0 : transition.enter) != null ? _a : withDelay.enter(TRANSITION_DEFAULTS.enter, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.enter
    };
  },
  exit: ({
    transition,
    transitionEnd,
    delay
  } = {}) => {
    var _a;
    return {
      opacity: 0,
      transition: (_a = transition == null ? void 0 : transition.exit) != null ? _a : withDelay.exit(TRANSITION_DEFAULTS.exit, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.exit
    };
  }
};
var fadeConfig = {
  initial: "exit",
  animate: "enter",
  exit: "exit",
  variants: variants$3
};
var Fade = /*#__PURE__*/forwardRef(function Fade2(props, ref) {
  const {
      unmountOnExit,
      in: isOpen,
      className,
      transition,
      transitionEnd,
      delay
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded2);
  const animate = isOpen || unmountOnExit ? "enter" : "exit";
  const show = unmountOnExit ? isOpen && unmountOnExit : true;
  const custom = {
    transition,
    transitionEnd,
    delay
  };
  return /* @__PURE__ */jsx(AnimatePresence, {
    custom,
    children: show && /* @__PURE__ */jsx(motion.div, Object.assign({
      ref,
      className: cx("chakra-fade", className),
      custom
    }, fadeConfig, {
      animate
    }, rest))
  });
});
Fade.displayName = "Fade";
var variants$2 = {
  exit: ({
    reverse,
    initialScale,
    transition,
    transitionEnd,
    delay
  }) => {
    var _a;
    return Object.assign({
      opacity: 0
    }, reverse ? {
      scale: initialScale,
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.exit
    } : {
      transitionEnd: Object.assign({
        scale: initialScale
      }, transitionEnd == null ? void 0 : transitionEnd.exit)
    }, {
      transition: (_a = transition == null ? void 0 : transition.exit) != null ? _a : withDelay.exit(TRANSITION_DEFAULTS.exit, delay)
    });
  },
  enter: ({
    transitionEnd,
    transition,
    delay
  }) => {
    var _a;
    return {
      opacity: 1,
      scale: 1,
      transition: (_a = transition == null ? void 0 : transition.enter) != null ? _a : withDelay.enter(TRANSITION_DEFAULTS.enter, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.enter
    };
  }
};
var scaleFadeConfig = {
  initial: "exit",
  animate: "enter",
  exit: "exit",
  variants: variants$2
};
var ScaleFade = /*#__PURE__*/forwardRef(function ScaleFade2(props, ref) {
  const {
      unmountOnExit,
      in: isOpen,
      reverse = true,
      initialScale = 0.95,
      className,
      transition,
      transitionEnd,
      delay
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded3);
  const show = unmountOnExit ? isOpen && unmountOnExit : true;
  const animate = isOpen || unmountOnExit ? "enter" : "exit";
  const custom = {
    initialScale,
    reverse,
    transition,
    transitionEnd,
    delay
  };
  return /* @__PURE__ */jsx(AnimatePresence, {
    custom,
    children: show && /* @__PURE__ */jsx(motion.div, Object.assign({
      ref,
      className: cx("chakra-offset-slide", className)
    }, scaleFadeConfig, {
      animate,
      custom
    }, rest))
  });
});
ScaleFade.displayName = "ScaleFade";
var variants$1 = {
  initial: ({
    offsetX,
    offsetY,
    transition,
    transitionEnd,
    delay
  }) => {
    var _a;
    return {
      opacity: 0,
      x: offsetX,
      y: offsetY,
      transition: (_a = transition == null ? void 0 : transition.exit) != null ? _a : withDelay.exit(TRANSITION_DEFAULTS.exit, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.exit
    };
  },
  enter: ({
    transition,
    transitionEnd,
    delay
  }) => {
    var _a;
    return {
      opacity: 1,
      x: 0,
      y: 0,
      transition: (_a = transition == null ? void 0 : transition.enter) != null ? _a : withDelay.enter(TRANSITION_DEFAULTS.enter, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.enter
    };
  },
  exit: ({
    offsetY,
    offsetX,
    transition,
    transitionEnd,
    reverse,
    delay
  }) => {
    var _a;
    const offset = {
      x: offsetX,
      y: offsetY
    };
    return Object.assign({
      opacity: 0,
      transition: (_a = transition == null ? void 0 : transition.exit) != null ? _a : withDelay.exit(TRANSITION_DEFAULTS.exit, delay)
    }, reverse ? Object.assign({}, offset, {
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.exit
    }) : {
      transitionEnd: Object.assign({}, offset, transitionEnd == null ? void 0 : transitionEnd.exit)
    });
  }
};
var slideFadeConfig = {
  initial: "initial",
  animate: "enter",
  exit: "exit",
  variants: variants$1
};
var SlideFade = /*#__PURE__*/forwardRef(function SlideFade2(props, ref) {
  const {
      unmountOnExit,
      in: isOpen,
      reverse = true,
      className,
      offsetX = 0,
      offsetY = 8,
      transition,
      transitionEnd,
      delay
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded4);
  const show = unmountOnExit ? isOpen && unmountOnExit : true;
  const animate = isOpen || unmountOnExit ? "enter" : "exit";
  const custom = {
    offsetX,
    offsetY,
    reverse,
    transition,
    transitionEnd,
    delay
  };
  return /* @__PURE__ */jsx(AnimatePresence, {
    custom,
    children: show && /* @__PURE__ */jsx(motion.div, Object.assign({
      ref,
      className: cx("chakra-offset-slide", className),
      custom
    }, slideFadeConfig, {
      animate
    }, rest))
  });
});
SlideFade.displayName = "SlideFade";
var defaultTransition = {
  exit: {
    duration: 0.15,
    ease: TRANSITION_EASINGS.easeInOut
  },
  enter: {
    type: "spring",
    damping: 25,
    stiffness: 180
  }
};
var variants = {
  exit: ({
    direction,
    transition,
    transitionEnd,
    delay
  }) => {
    var _a;
    const {
      exit: exitStyles
    } = getSlideTransition({
      direction
    });
    return Object.assign({}, exitStyles, {
      transition: (_a = transition == null ? void 0 : transition.exit) != null ? _a : withDelay.exit(defaultTransition.exit, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.exit
    });
  },
  enter: ({
    direction,
    transitionEnd,
    transition,
    delay
  }) => {
    var _a;
    const {
      enter: enterStyles
    } = getSlideTransition({
      direction
    });
    return Object.assign({}, enterStyles, {
      transition: (_a = transition == null ? void 0 : transition.enter) != null ? _a : withDelay.enter(defaultTransition.enter, delay),
      transitionEnd: transitionEnd == null ? void 0 : transitionEnd.enter
    });
  }
};
var Slide = /*#__PURE__*/forwardRef(function Slide2(props, ref) {
  const {
      direction = "right",
      style,
      unmountOnExit,
      in: isOpen,
      className,
      transition,
      transitionEnd,
      delay,
      motionProps
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded5);
  const transitionStyles = getSlideTransition({
    direction
  });
  const computedStyle = Object.assign({
    position: "fixed"
  }, transitionStyles.position, style);
  const show = unmountOnExit ? isOpen && unmountOnExit : true;
  const animate = isOpen || unmountOnExit ? "enter" : "exit";
  const custom = {
    transitionEnd,
    transition,
    direction,
    delay
  };
  return /* @__PURE__ */jsx(AnimatePresence, {
    custom,
    children: show && /* @__PURE__ */jsx(motion.div, Object.assign({}, rest, {
      ref,
      initial: "exit",
      className: cx("chakra-slide", className),
      animate,
      exit: "exit",
      custom,
      variants,
      style: computedStyle
    }, motionProps))
  });
});
Slide.displayName = "Slide";
export { Collapse, TRANSITION_EASINGS as EASINGS, Fade, ScaleFade, Slide, SlideFade, fadeConfig, getSlideTransition, scaleFadeConfig, slideFadeConfig, withDelay };